"use client";

import React, { useState, useEffect } from "react";
import Image from "next/image";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import Projects from "../data/projects";
import { useServiceData } from "../hooks/useServiceData";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Header text for the section
export const Header = ({ title, subtitle }) => {
  return (
    <div className="max-w-7xl relative mx-auto py-20 md:py-40 px-4 w-full left-0 top-0">
      <h2 className="text-2xl md:text-6xl font-bold text-white">
        {title || "Selected Case Studies"}
      </h2>
      <p className="max-w-2xl text-base md:text-xl mt-6 text-neutral-200">
        {subtitle || "We build products end‑to‑end. Explore highlights from our recent work."}
      </p>
    </div>
  );
};

// Individual card displayed in each moving row
export const ProductCard = ({ product, translate }) => {
  return (
    <motion.div
      style={{ x: translate }}
      whileHover={{ y: -20 }}
      key={product.title}
      className="group/product h-80 md:h-96 w-[22rem] md:w-[26rem] lg:w-[28rem] xl:w-[30rem] relative shrink-0 bg-neutral-900"
    >
      <a href={product.link} className="block relative h-full w-full group-hover/product:shadow-2xl">
        <Image
          src={product.thumbnail}
          alt={product.title}
          fill
          unoptimized={product.thumbnail?.includes("localhost") || product.thumbnail?.includes("127.0.0.1")}
          sizes="(max-width: 768px) 90vw, (max-width: 1280px) 40vw, 30vw"
          className="object-contain object-center inset-0"
          priority={false}
        />
      </a>
      <div className="absolute inset-0 h-full w-full opacity-0 group-hover/product:opacity-80 bg-black pointer-events-none" />
      <h2 className="absolute bottom-4 left-4 opacity-0 group-hover/product:opacity-100 text-white text-lg font-semibold">
        {product.title}
      </h2>
      <span className="absolute top-4 right-4 opacity-0 group-hover/product:opacity-100 text-xs tracking-wide uppercase bg-white/10 border border-white/20 text-white rounded-full px-2 py-1">
        Case Study
      </span>
    </motion.div>
  );
};

// The main parallax section composed of 3 rows that counter‑scroll
export const HeroParallax = ({ products, title, subtitle }) => {
  // Distribute products evenly across three rows to support any list length
  const rows = Array.from({ length: 3 }, (_, idx) =>
    products.filter((_, i) => i % 3 === idx)
  );

  // Repeat each row to extend horizontally and avoid empty side spaces
  const repeatTimes = 3;
  const repeat = (arr, times) => Array.from({ length: times }, () => arr).flat();
  const row0 = repeat(rows[0], repeatTimes);
  const row1 = repeat(rows[1], repeatTimes);
  const row2 = repeat(rows[2], repeatTimes);

  const ref = React.useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const springConfig = { stiffness: 300, damping: 30, bounce: 100 };

  const translateX = useSpring(
    useTransform(scrollYProgress, [0, 1], [0, 1000]),
    springConfig
  );
  const translateXReverse = useSpring(
    useTransform(scrollYProgress, [0, 1], [0, -1000]),
    springConfig
  );
  const rotateX = useSpring(
    useTransform(scrollYProgress, [0, 0.2], [15, 0]),
    springConfig
  );
  const opacity = useSpring(
    useTransform(scrollYProgress, [0, 0.2], [0.2, 1]),
    springConfig
  );
  const rotateZ = useSpring(
    useTransform(scrollYProgress, [0, 0.2], [20, 0]),
    springConfig
  );
  const translateY = useSpring(
    useTransform(scrollYProgress, [0, 0.2], [-700, 500]),
    springConfig
  );

  return (
    <div
      ref={ref}
      className={cn(
        "h-[300vh] py-40 overflow-hidden antialiased relative flex flex-col",
        "[perspective:1000px] [transform-style:preserve-3d]"
      )}
    >
      <Header title={title} subtitle={subtitle} />
      <motion.div
        style={{ rotateX, rotateZ, translateY, opacity }}
        className="relative"
      >
        <motion.div className="flex flex-row-reverse space-x-reverse space-x-10 md:space-x-16 xl:space-x-20 mb-16 md:mb-20">
          {row0.map((product, idx) => (
            <ProductCard product={product} translate={translateX} key={`${product.title}-r0-${idx}`} />
          ))}
        </motion.div>
        <motion.div className="flex flex-row mb-16 md:mb-20 space-x-10 md:space-x-16 xl:space-x-20">
          {row1.map((product, idx) => (
            <ProductCard product={product} translate={translateXReverse} key={`${product.title}-r1-${idx}`} />
          ))}
        </motion.div>
        <motion.div className="flex flex-row-reverse space-x-reverse space-x-10 md:space-x-16 xl:space-x-20">
          {row2.map((product, idx) => (
            <ProductCard product={product} translate={translateX} key={`${product.title}-r2-${idx}`} />
          ))}
        </motion.div>
      </motion.div>
    </div>
  );
};

export default function ProjectsShowcase({ dynamicData }) {
  const { data: hookData } = useServiceData();
  const [products, setProducts] = useState([]);
  const [headerContent, setHeaderContent] = useState({
    title: "Selected Case Studies",
    subtitle: "We build products end to end. Explore highlights from our recent work.",
  });

  useEffect(() => {
    // Priority: Props > Hook Data
    const portfolio = dynamicData || hookData?.portfolio;

    if (portfolio && portfolio.items && portfolio.items.length > 0) {
      const mapped = portfolio.items.map((it) => {
        let rawThumbnail = it.images && it.images.length > 0 ? it.images[0].url : (it.image || "");
        let thumbnailUrl = typeof rawThumbnail === 'string' ? rawThumbnail : (rawThumbnail?.url || '');
        
        // Ensure relative URLs are absolute by prepending backend URL
        if (thumbnailUrl && typeof thumbnailUrl === 'string' && thumbnailUrl.startsWith('/')) {
           const apiUrl = (process.env.NEXT_PUBLIC_API_URL || '').replace(/\/api\/?$/, '').replace(/\/$/, '') || "";
           thumbnailUrl = `${apiUrl}${thumbnailUrl}`;
        }

        return {
          title: it.title || "Project",
          link: it.link || "#",
          thumbnail: thumbnailUrl,
        };
      });
      setProducts(mapped);
      setHeaderContent({
        title: portfolio.title || "Selected Case Studies",
        subtitle: portfolio.subtitle || "We build products end to end. Explore highlights from our recent work.",
      });
    } else {
      // Fallback to static data
      setProducts(Projects.map((p) => ({
        title: p.title,
        link: p.href,
        thumbnail: p.src,
      })));
    }
  }, [dynamicData, hookData]);

  if (products.length === 0) return null;

  return <HeroParallax products={products} title={headerContent.title} subtitle={headerContent.subtitle} />;
}
